import { Link, useParams, useNavigate } from 'react-router-dom';
import { PencilIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { useProject, useProjectCSATTimeline, useDeleteProject } from '../../hooks/useProjects';
import { surveyService } from '../../services/survey.service';
import type { ProjectStatus, DispatchFilters } from '../../types';

const statusColors: Record<ProjectStatus, string> = {
  planning: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  paused: 'bg-orange-100 text-orange-800',
};

const statusLabels: Record<ProjectStatus, string> = {
  planning: 'Planning',
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
  paused: 'Paused',
};

function useProjectDispatches(projectId: string) {
  return useQuery({
    queryKey: ['dispatches', { project_id: projectId }],
    queryFn: () => surveyService.getDispatches({ project_id: projectId } as DispatchFilters),
    enabled: !!projectId,
  });
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProject(id!);
  const { data: csatTimeline } = useProjectCSATTimeline(id!);
  const deleteProject = useDeleteProject();

  const handleDelete = () => {
    if (project && window.confirm(`Are you sure you want to delete project "${project.name}"?`)) {
      deleteProject.mutate(project.id, {
        onSuccess: () => navigate('/projects'),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Loading project...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-red-600 p-4">
        Error loading project: {(error as Error)?.message || 'Not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/projects')}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600"
            aria-label="Back to projects"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-sm text-gray-500">
              Client: {project.client_name || 'Unknown'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/projects/${project.id}/edit`}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
            disabled={deleteProject.isPending}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Project Info */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Status</dt>
              <dd>
                <span
                  className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusColors[project.status]}`}
                >
                  {statusLabels[project.status]}
                </span>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Start Date</dt>
              <dd className="text-sm text-gray-900">
                {project.start_date
                  ? format(new Date(project.start_date), 'MMM d, yyyy')
                  : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">End Date</dt>
              <dd className="text-sm text-gray-900">
                {project.end_date
                  ? format(new Date(project.end_date), 'MMM d, yyyy')
                  : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">CSAT Score</dt>
              <dd className="text-sm font-medium">
                {project.current_csat_score != null ? (
                  <span
                    className={
                      project.current_csat_score >= 4
                        ? 'text-green-600'
                        : project.current_csat_score >= 3
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }
                  >
                    {Number(project.current_csat_score).toFixed(1)} / 5.0
                  </span>
                ) : (
                  <span className="text-gray-400">No data</span>
                )}
              </dd>
            </div>
            {project.description && (
              <div className="pt-2 border-t">
                <dt className="text-sm text-gray-500 mb-1">Description</dt>
                <dd className="text-sm text-gray-900">{project.description}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* CSAT Chart */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">CSAT Trend</h2>
          {csatTimeline && csatTimeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={csatTimeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                  fontSize={12}
                />
                <YAxis domain={[1, 5]} fontSize={12} />
                <Tooltip
                  labelFormatter={(value) => format(new Date(value as string), 'MMM d, yyyy')}
                  formatter={(value: number) => [value.toFixed(2), 'CSAT']}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot={{ fill: '#4f46e5', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-sm text-gray-400">
              No CSAT data available yet.
            </div>
          )}
        </div>
      </div>

      {/* Assigned Contacts */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Assigned Contacts</h2>
        {project.contacts && project.contacts.length > 0 ? (
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Position
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {project.contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {contact.first_name} {contact.last_name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {contact.email}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {contact.position || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          contact.is_active !== false
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {contact.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No contacts assigned to this project.</p>
        )}
      </div>

      {/* Dispatch History */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Dispatch History</h2>
          <Link
            to={`/surveys/dispatches?project_id=${project.id}`}
            className="text-sm text-indigo-600 hover:text-indigo-900"
          >
            View All
          </Link>
        </div>
        <DispatchHistory projectId={project.id} />
      </div>
    </div>
  );
}

function DispatchHistory({ projectId }: { projectId: string }) {
  const { data, isLoading } = useProjectDispatches(projectId);

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading dispatches...</p>;
  }

  if (!data || !data.data || data.data.length === 0) {
    return <p className="text-sm text-gray-500">No survey dispatches for this project yet.</p>;
  }

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Template
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Sent
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Responses
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Avg Score
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.data.map((dispatch) => (
            <tr key={dispatch.id} className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-4 py-3">
                <Link
                  to={`/surveys/dispatches/${dispatch.id}`}
                  className="text-sm text-indigo-600 hover:text-indigo-900"
                >
                  {dispatch.template_name || 'Unknown Template'}
                </Link>
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 capitalize">
                  {dispatch.status}
                </span>
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                {dispatch.sent_at
                  ? format(new Date(dispatch.sent_at), 'MMM d, yyyy')
                  : '—'}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                {dispatch.completed_count ?? 0} / {dispatch.total_invitations ?? 0}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                {dispatch.average_score != null
                  ? Number(dispatch.average_score).toFixed(1)
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
